import React, {ReactElement, useState, useEffect} from 'react';
import {Menu} from 'acud';
import * as tmp from 'react-router-dom';
import PropTypes from 'prop-types';
import './index.less';

const {SubMenu, MenuHead} = Menu;

// ignore waring `"export 'useNavigate' (imported as 'rc') was not found in 'react-router-dom'`
const rc = tmp as any;

interface MenuItem {
    text: string;
    type: 'submenu' | 'item';
    key: string;
    iconDefault?: ReactElement;
    iconActive?: ReactElement;
    children?: MenuItem[];
    selectKey?: string;
    externalLink?: string;
    externalBlank?: boolean;
}

export interface SidebarProps {
    menuItems: MenuItem[];
    moduleName?: string;
}

// 递归菜单组件
const RecursiveMenu = ({items, activeFatherKey, level = 1}) => {
    return items.map(item => {
        const icon = {icon: item.key === activeFatherKey ? item.iconActive : item.iconDefault};
        if (item.type === 'submenu') {
            return (
                <SubMenu key={item.key} title={item.text} eventKey={item.key} {...icon}>
                    {/* 递归调用处理子菜单，层级+1 */}
                    <RecursiveMenu items={item.children} activeFatherKey={activeFatherKey} level={level + 1} />
                </SubMenu>
            );
        }

        return (
            <Menu.Item key={item.key} eventKey={item.key} {...icon} className={level === 3 ? 'gray-text' : ''}>
                {item.text}
            </Menu.Item>
        );
    });
};

const SideBar: React.FunctionComponent<SidebarProps> = props => {
    const {menuItems, moduleName} = props;

    const navigate = rc.useNavigate?.();
    const history = rc.useHistory?.();

    const location = rc.useLocation?.();
    const currentPath = location.pathname;

    const findActiveMenuItem = (items, currentPath) => {
        for (const item of items) {
            if (currentPath.includes(item.selectKey || item.key)) {
                return item;
            }
            if (item.children) {
                const found = findActiveMenuItem(item.children, currentPath);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    };

    // 根据key,查找item
    const findItemByKey = (items, targetKey) => {
        for (const item of items) {
            if (item.key === targetKey) {
                return item;
            }
            if (item.children) {
                const found = findItemByKey(item.children, targetKey);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    };

    const findParentByKey = (menuItems, targetKey) => {
        let ancestors = [{key: ''}];

        const traverse = (items, path) => {
            for (const item of items) {
                // 创建当前路径：继承之前的路径并添加当前项
                const currentPath = [...path, item];

                if (item.key === targetKey) {
                    // 找到目标时，返回
                    ancestors = currentPath;
                    return true;
                }

                // 递归遍历子菜单，并传递更新后的路径
                if (item.children && traverse(item.children, currentPath)) {
                    return true; // 如果子节点中找到目标，提前退出
                }
            }
            return false;
        };

        traverse(menuItems, []); // 初始路径为空
        return ancestors;
        // return ancestors[0];
    };

    const computeActive = () => {
        const activeMenuItem = findActiveMenuItem(menuItems, currentPath.split('?')[0]);
        const activeFatherItem = activeMenuItem ? findParentByKey(menuItems, activeMenuItem.key) : [{key: ''}];
        return {
            activeMenuItem,
            activeFatherItem
        };
    };

    const {activeMenuItem, activeFatherItem} = computeActive();
    const [activeKey, setActiveKey] = useState(activeMenuItem?.key); // 当前激活路由
    const [activeFatherKey, setActiveFatherKey] = useState(activeFatherItem?.map(item => item.key));

    useEffect(() => {
        const {activeMenuItem, activeFatherItem} = computeActive();
        setActiveKey(activeMenuItem ? activeMenuItem?.key : '');
        setActiveFatherKey(activeFatherItem ? activeFatherItem?.map(item => item.key) : ['']);
    }, [currentPath]); // 确保依赖数组正确

    const handleClick = e => {
        const parentItem = findParentByKey(menuItems, e.key) || {key: ''};
        setActiveFatherKey(parentItem?.map(item => item.key));
        const curItem = findItemByKey(menuItems, e.key);

        try {
            if (curItem.externalLink) {
                curItem.externalBlank
                    ? window.open(curItem.externalLink)
                    : window.location.href = curItem.externalLink;
                return;
            }
            else if (history) {
                history.push(`/${e.key}`); // v5 语法
            }
            else if (navigate) {
                navigate(`/${e.key}`);
            }
        }
        catch (error) {
            console.error('Routing failed:', error);
        }
    };

    const MenuItems = () => {
        return <RecursiveMenu items={menuItems} activeFatherKey={activeFatherKey} />;
    };

    return (
        <div className="app-menu-container">
            <Menu
                onClick={handleClick}
                mode="inline"
                scope="global"
                defaultOpenKeys={activeFatherKey || ['/']}
                defaultSelectedKeys={[activeKey]}
                className="app-menu"
            >
                <MenuHead>{moduleName}</MenuHead>
                <MenuItems />
            </Menu>
        </div>
    );
};

SideBar.propTypes = {
    menuItems: PropTypes.array.isRequired,
    moduleName: PropTypes.string
};

export default SideBar;
